import type { OnInit } from '@angular/core';
import { Component, ElementRef, EventEmitter, HostListener, inject, Output, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AnalyticsConsentService } from '@core-mkt/services/analytics/analytics-consent.service';
import { PrivacyCheckService } from '@core-mkt/services/privacy-check/privacy-check.service';
import { SegmentService } from '@core-mkt/services/segment/segment.service';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

/** Longest the opt-out reload waits for `Segment Consent Preference Updated` to dispatch. */
export const CONSENT_EVENT_FLUSH_TIMEOUT_MS = 500;

@Component({
  selector: 'privacy-manager',
  templateUrl: './privacy-manager.component.html',
  styleUrls: ['./privacy-manager.component.scss'],
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
})
export class PrivacyManagerComponent implements OnInit {
  @Output() hideModal = new EventEmitter<boolean>();
  @ViewChild('privacyManager') privacyManager: ElementRef<HTMLElement>;
  @ViewChild('privacyManagerOverlay') privacyManagerOverlay: ElementRef<HTMLElement>;
  doNotSell = 0;
  faTimes = faTimes;
  private readonly document = inject(DOCUMENT);
  /** Whether ad destinations were already off when the page loaded (decides the reload on save). */
  private optedOutOnOpen = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.privacyManagerOverlay.nativeElement.contains(event.target as Node) &&
      !this.privacyManager.nativeElement.contains(event.target as Node)
    ) {
      this.close();
    }
  }

  constructor(
    public privacyCheckService: PrivacyCheckService,
    private readonly segmentService: SegmentService,
    private readonly analyticsConsentService: AnalyticsConsentService,
  ) {}

  get title(): string {
    return 'Privacy Manager';
  }

  get privacyContent(): string {
    return 'Under certain US state laws, you have the right to opt out of the sale or sharing of your personal information. While we do not sell your personal information, we do share it with our partners to deliver personalized ads across different websites and apps.';
  }

  get privacyContentDirections(): string {
    return 'Your privacy is a priority for us. You can manage your privacy preferences below. Switching the toggle to “OFF” or gray indicates “Do not sell or share my personal information”';
  }

  ngOnInit(): void {
    // Show the state actually being enforced: GPC opts the visitor out even over a stored '0'.
    this.optedOutOnOpen = this.analyticsConsentService.isOptedOut();
    this.doNotSell = this.optedOutOnOpen ? 1 : 0;
  }

  updateLightSwitch(checked: boolean): void {
    this.doNotSell = checked ? 0 : 1;
  }

  savePrivacySetting() {
    this.privacyCheckService.updatePrivacyCheck(this.doNotSell);

    // Track consent preference update to Segment
    const tracked = this.segmentService.callAnalyticsMethod(
      'track',
      'Segment Consent Preference Updated',
      {
        // TODO: 🔥 Waiting for Segment rep on final format of this call
      },
      {
        consent: {
          categoryPreferences: {
            DataSharing: this.doNotSell === 0,
          },
        },
      },
    );

    this.close();

    // Opting out mid-session: Segment events are re-checked per event, but ad tags that already
    // loaded (Meta Pixel, GTM and its own triggers) keep running until the page is reloaded.
    // Wait (bounded) for the consent event to dispatch first, or the unload can drop it.
    // Opting back in does not reload: ad destinations stay off until the next navigation (fails safe).
    if (this.doNotSell === 1 && !this.optedOutOnOpen) {
      const timeout = new Promise<void>((resolve) => setTimeout(resolve, CONSENT_EVENT_FLUSH_TIMEOUT_MS));
      void Promise.race([tracked ?? Promise.resolve(), timeout])
        .catch(() => undefined)
        .then(() => this.reloadPage());
    }
  }

  private reloadPage(): void {
    this.document.defaultView?.location.reload();
  }

  close() {
    this.hideModal.emit(false);
  }
}
